import { USStates } from "../../../create-loan/constants/loanCreationDropDownValues";
import {
  BANK_ZIPCODE_LENGTH,
  getBankAddressMandatoryFrom
} from "../../../stores/drawRequest/drawRequest.constants";
import {
  accountNumberLooksMasked,
  bankDetailsLookLikeMaskedEcho,
  isValidRoutingNumber
} from "./bankRoutingValidation";

export const BANK_STATE_INVALID_MESSAGE = "Beneficiary State must be valid.";
export const BANK_REQUIRED_FIELDS_MESSAGE =
  "Please fill all required fields before saving.";
export const BANK_FIELD_REQUIRED_MESSAGE = "This is a mandatory field.";
export const ROUTING_LENGTH_MESSAGE = "Routing number must be of 9 digits";
export const ACCOUNT_NUMBER_NUMERIC_MESSAGE = "Account Number must be numeric.";
export const ZIPCODE_LENGTH_MESSAGE = `Zip Code must be ${BANK_ZIPCODE_LENGTH} digits.`;

const BENEFICIARY_STATE_CODES: ReadonlySet<string> = new Set(USStates);

export type BankAddressIssue = "cityRequired" | "stateRequired" | "stateInvalid";

const toAddressString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value).trim();
  return "";
};

const getDrawSubmittedAt = (drawObj: any): string =>
  drawObj?.requestedDate || "";

/**
 * Mirrors isBankAddressMandatoryForDraw in draw-service: same cut-off, same >=
 * comparison against the draw's submitted date (requestedDate / Date Requested).
 * When application-config has no cut-off (empty/missing), always false.
 * An absent or unparseable submitted date counts as past the cut-off (mandatory)
 * only when a cut-off is configured.
 */
export const isBankAddressMandatory = (drawObj: any): boolean => {
  const cutoff = getBankAddressMandatoryFrom();
  if (cutoff == null) return false;
  const submitted = getDrawSubmittedAt(drawObj);
  if (!submitted) return true;
  const submittedAt = new Date(submitted).getTime();
  if (Number.isNaN(submittedAt)) return true;
  return submittedAt >= cutoff.getTime();
};

const isDraftStage = (stage: unknown): boolean =>
  String(stage ?? "").toLowerCase() === "draft";

/**
 * Frontend gate for city/state/zip/country checks. Feature off (no cut-off) =>
 * never. Drafts always validate when the feature is on; other stages use the
 * submitted-date cut-off (same as draw-service).
 */
export const shouldValidateBankAddress = (drawObj: any): boolean => {
  if (getBankAddressMandatoryFrom() == null) return false;
  if (isDraftStage(drawObj?.stage)) return true;
  return isBankAddressMandatory(drawObj);
};

export const isValidBeneficiaryState = (value: unknown): boolean => {
  const code = toAddressString(value).toUpperCase();
  return /^[A-Z]{2}$/.test(code) && BENEFICIARY_STATE_CODES.has(code);
};

export const getBankAddressIssues = (
  selectedBankDetails: any,
  addressMandatory: boolean
): BankAddressIssue[] => {
  if (!addressMandatory) return [];
  const issues: BankAddressIssue[] = [];
  const city = toAddressString(selectedBankDetails?.city);
  const state = toAddressString(selectedBankDetails?.state);
  if (!city) issues.push("cityRequired");
  if (!state) issues.push("stateRequired");
  if (state && !isValidBeneficiaryState(state)) issues.push("stateInvalid");
  return issues;
};

export const bankAddressWarningMessage = (
  issues: BankAddressIssue[]
): string | null => {
  if (issues.length === 0) return null;
  if (
    issues.includes("stateInvalid") &&
    !issues.includes("stateRequired") &&
    !issues.includes("cityRequired")
  ) {
    return BANK_STATE_INVALID_MESSAGE;
  }
  return BANK_REQUIRED_FIELDS_MESSAGE;
};

export const beneficiaryCityError = (
  city: unknown,
  addressMandatory: boolean
): string | null => {
  if (!addressMandatory) return null;
  return toAddressString(city) ? null : BANK_FIELD_REQUIRED_MESSAGE;
};

export const beneficiaryStateError = (
  state: unknown,
  addressMandatory: boolean
): string | null => {
  if (!addressMandatory) return null;
  const code = toAddressString(state);
  if (!code) return BANK_FIELD_REQUIRED_MESSAGE;
  if (!isValidBeneficiaryState(code)) return BANK_STATE_INVALID_MESSAGE;
  return null;
};

export const routingNumberError = (
  value: unknown,
  bankDetailObj?: any
): string | null => {
  const routing = toAddressString(value);
  if (!routing) return null;
  // Asterisk display mask — never a typed routing.
  if (routing.includes("*")) return null;
  // Last-4 / masked echo: only skip when the rest of the payload corroborates a mask.
  // A lone 4-digit routing with plaintext account fields must fail the 9-digit rule.
  if (
    bankDetailObj != null &&
    bankDetailsLookLikeMaskedEcho({ ...bankDetailObj, routing })
  ) {
    return null;
  }
  return isValidRoutingNumber(routing) ? null : ROUTING_LENGTH_MESSAGE;
};

/**
 * Excel can store alphanumeric account numbers that the UI digit-strip never
 * sees. A masked last-4 / X-prefix is not a typed value and is skipped.
 */
export const accountNumberNumericError = (value: unknown): string | null => {
  const account = toAddressString(value);
  if (!account || accountNumberLooksMasked(account)) return null;
  return /^\d+$/.test(account) ? null : ACCOUNT_NUMBER_NUMERIC_MESSAGE;
};

export const zipcodeLengthError = (value: unknown): string | null => {
  const zip = toAddressString(value);
  if (!zip) return null;
  return zip.length === BANK_ZIPCODE_LENGTH ? null : ZIPCODE_LENGTH_MESSAGE;
};

const sensitiveFieldValue = (
  editValue: string | undefined,
  storedValue: unknown
): string => editValue ?? toAddressString(storedValue);

/**
 * Inline red copy under a bank field after Save/Submit (isSubmitClicked).
 * Empty required fields share BANK_FIELD_REQUIRED_MESSAGE; format issues use
 * the same messages as getBankDetailsSaveWarning.
 */
export const bankFieldInlineError = (
  field: string,
  bankDetailObj: any,
  editDecrypted: { accountNumber?: string; routing?: string } = {},
  addressMandatory = false
): string | null => {
  if (field === "accountNumber") {
    const account = sensitiveFieldValue(
      editDecrypted.accountNumber,
      bankDetailObj?.accountNumber
    );
    return account
      ? accountNumberNumericError(account)
      : BANK_FIELD_REQUIRED_MESSAGE;
  }
  if (field === "routing") {
    const routing = sensitiveFieldValue(
      editDecrypted.routing,
      bankDetailObj?.routing
    );
    return routing
      ? routingNumberError(routing, bankDetailObj)
      : BANK_FIELD_REQUIRED_MESSAGE;
  }
  if (field === "bankName" || field === "accountName") {
    return bankDetailObj?.[field] ? null : BANK_FIELD_REQUIRED_MESSAGE;
  }
  if (field === "city") {
    return beneficiaryCityError(bankDetailObj?.city, addressMandatory);
  }
  if (field === "state") {
    return beneficiaryStateError(bankDetailObj?.state, addressMandatory);
  }
  if (field === "zipcode") {
    return addressMandatory
      ? zipcodeLengthError(bankDetailObj?.zipcode)
      : null;
  }
  return null;
};

/**
 * Client-side bank checks that must pass before any draw PUT (Save, Submit, or
 * draft autosave). Uses visibleRouting when the edit input has published it;
 * otherwise the stored routing, unless that value is a mask of a real 9-digit
 * number that has not been decrypted yet.
 */
export const getBankDetailsSaveWarning = (
  drawDetails: any,
  selectedBankDetails: any,
  visibleRouting?: string
): string | null => {
  const accountError = accountNumberNumericError(
    selectedBankDetails?.accountNumber
  );
  if (accountError) return accountError;
  const visible = toAddressString(visibleRouting);
  if (visible) {
    const visibleError = routingNumberError(visible, selectedBankDetails);
    if (visibleError) return visibleError;
  } else {
    const storedError = routingNumberError(
      selectedBankDetails?.routing,
      selectedBankDetails
    );
    if (storedError) return storedError;
  }
  const addressMandatory = shouldValidateBankAddress(drawDetails);
  if (addressMandatory) {
    const zipError = zipcodeLengthError(selectedBankDetails?.zipcode);
    if (zipError) return zipError;
  }
  return bankAddressWarningMessage(
    getBankAddressIssues(selectedBankDetails, addressMandatory)
  );
};
